import React from 'react';
import { Label } from '../ui/label';

/**
 * Smart Entry Flow picker. Additive — when entry_mode is 'standard' (default),
 * the existing release_method behavior runs unchanged. When 'smart_flow' is
 * selected, the host picks one of three preset transitions; the preset
 * computes time-based phases relative to the event start.
 *
 * NOTE: Phases compute their `until` ISO strings only when `eventDate` and
 * `eventTime` are both set. If they are missing, the host sees a hint and
 * cannot lock in a preset until basics are filled.
 */

const PRESETS = {
  strict_control: {
    label: 'Strict Control',
    description: 'Approval required for every guest, all the way through the event.',
    build: (_eventStart) => ([
      { until: null, mode: 'paid_approval', price_modifier: 0 },
    ]),
  },
  balanced: {
    label: 'Balanced (default)',
    description: 'Approval until 24h out → instant pay until event start → cash/RSVP at the door.',
    build: (eventStart) => ([
      { until: addHours(eventStart, -24).toISOString(), mode: 'paid_approval', price_modifier: 0 },
      { until: eventStart.toISOString(),               mode: 'paid_auto',     price_modifier: 0 },
      { until: null,                                    mode: 'cash_rsvp',    price_modifier: 0 },
    ]),
  },
  open_door: {
    label: 'Open Door',
    description: 'Instant pay before event → cash/RSVP at the door once doors open.',
    build: (eventStart) => ([
      { until: eventStart.toISOString(), mode: 'paid_auto',  price_modifier: 0 },
      { until: null,                      mode: 'cash_rsvp', price_modifier: 0 },
    ]),
  },
};

function addHours(dt, h) {
  const out = new Date(dt.getTime());
  out.setHours(out.getHours() + h);
  return out;
}

function buildEventStart(date, time) {
  if (!date || !time) return null;
  // Local-time interpretation matches the existing form fields
  const dt = new Date(`${date}T${time}`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function detectPreset(flow) {
  if (!Array.isArray(flow) || flow.length === 0) return null;
  if (flow.length === 1 && flow[0]?.mode === 'paid_approval') return 'strict_control';
  if (flow.length === 3
    && flow[0]?.mode === 'paid_approval'
    && flow[1]?.mode === 'paid_auto'
    && flow[2]?.mode === 'cash_rsvp') return 'balanced';
  if (flow.length === 2
    && flow[0]?.mode === 'paid_auto'
    && flow[1]?.mode === 'cash_rsvp') return 'open_door';
  return null;
}

export default function SmartEntryFlowSection({ form, setForm }) {
  const entryMode = form.entry_mode || 'standard';
  const eventStart = buildEventStart(form.date, form.time);
  const activePreset = detectPreset(form.entry_flow);
  const basicsReady = !!eventStart;

  const handleModeChange = (next) => {
    if (next === 'standard') {
      setForm({ ...form, entry_mode: 'standard', entry_flow: null });
    } else {
      setForm({ ...form, entry_mode: 'smart_flow', entry_flow: form.entry_flow || null });
    }
  };

  const applyPreset = (key) => {
    if (!eventStart) return;
    const flow = PRESETS[key].build(eventStart);
    setForm({ ...form, entry_mode: 'smart_flow', entry_flow: flow });
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6" data-testid="entry-mode-section">
      <h2 className="text-xl font-semibold text-[#EAE7E1] mb-1">Entry Mode</h2>
      <p className="text-sm text-[#777] mb-4">
        Standard uses your Access Release Method above. Smart Flow shifts the rules
        as the event approaches — useful when you want approval early but a relaxed door later.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <button
          type="button"
          onClick={() => handleModeChange('standard')}
          className={`flex-1 px-4 py-3 rounded-sm text-left border transition ${
            entryMode === 'standard'
              ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#EAE7E1]'
              : 'border-[#1A1A1A] bg-[#050505] text-[#AFAFAF] hover:border-[#333]'
          }`}
          data-testid="entry-mode-standard"
        >
          <div className="font-semibold text-sm">Standard</div>
          <div className="text-xs text-[#777] mt-1">Use the Access Release Method above (default).</div>
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('smart_flow')}
          className={`flex-1 px-4 py-3 rounded-sm text-left border transition ${
            entryMode === 'smart_flow'
              ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#EAE7E1]'
              : 'border-[#1A1A1A] bg-[#050505] text-[#AFAFAF] hover:border-[#333]'
          }`}
          data-testid="entry-mode-smart-flow"
        >
          <div className="font-semibold text-sm">Smart Flow</div>
          <div className="text-xs text-[#777] mt-1">Approval → Instant pay → Cash/RSVP, on a timeline.</div>
        </button>
      </div>

      {entryMode === 'smart_flow' && (
        <div data-testid="smart-flow-presets">
          <Label className="text-sm text-[#AFAFAF]">Choose a preset</Label>
          {!basicsReady && (
            <p className="text-xs text-[#D4AF37] mt-1 mb-2" data-testid="smart-flow-needs-basics">
              Set the event date and start time above to enable presets.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
            {Object.entries(PRESETS).map(([key, preset]) => {
              const isActive = activePreset === key;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={!basicsReady}
                  onClick={() => applyPreset(key)}
                  className={`text-left px-4 py-3 rounded-sm border transition ${
                    isActive
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#EAE7E1]'
                      : 'border-[#1A1A1A] bg-[#050505] text-[#AFAFAF] hover:border-[#333]'
                  } ${!basicsReady ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid={`smart-flow-preset-${key.replace('_', '-')}`}
                >
                  <div className="font-semibold text-sm">{preset.label}</div>
                  <div className="text-xs text-[#777] mt-1 leading-relaxed">{preset.description}</div>
                </button>
              );
            })}
          </div>
          {Array.isArray(form.entry_flow) && form.entry_flow.length > 0 && (
            <div className="mt-4 text-xs text-[#777]" data-testid="smart-flow-summary">
              <span className="text-[#AFAFAF]">{form.entry_flow.length}</span> phase{form.entry_flow.length === 1 ? '' : 's'} configured.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
